import React from 'react';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import lock from 'four-nets/icons/svg/icon_lock.svg';
import Styled from 'styled';

import LinkView from 'lib/ui/link-view';
import computed from 'lib/mobx/computed';
import SVGIcon from 'lib/images/svg-icon/svg-icon';
import SVGPureIcon from 'lib/images/svg-pure-icon/svg-pure-icon';
import FormattedDateAgoView from 'lib/intl/formatted-date-ago-view';

import message from 'four-nets/icons/svg/icon_comment.svg';
import users from 'four-nets/icons/svg/icon_users.svg';
import user from 'four-nets/icons/svg/icon_user.svg';
import like from 'four-nets/icons/svg/icon_like.svg';
import arrowEnter from 'four-nets/icons/svg/icon_last.svg';
import { UserLink } from 'four-nets/user/user-link';

import { Thread } from '../forum';
import { ThreadLink } from '../thread-link';
import defaultStyles from './thread-row.scss';
import { URLBuilder } from 'url-builder/url-builder';

interface Options {
    thread: Thread;
}

export class ThreadViewModel {
    thread: Thread;
    link: ThreadLink;

    constructor(options: Options) {
        const { thread } = options;

        this.thread = thread;
        this.link = new ThreadLink({
            thread,
        });
    }

    @computed get lastMessageLink() {
        return new ThreadLink({
            thread: this.thread,
            params: {
                page: 'last',
                post: this.thread.lastMessageId,
            },
        });
    }

    @computed get title() {
        return this.thread.title;
    }

    @computed get messagesCount() {
        return this.thread.replyCount || 0;
    }

    @computed get author() {
        if (this.thread) {
            return {
                username: this.thread.lastMessageUsername,
                name: this.thread.lastMessageRealName,
            };
        }
        return null;
    }

    @computed get date() {
        return this.thread.lastMessageTime;
    }

    @computed get contributorsCount() {
        return this.thread.usersCount || 0;
    }

    @computed get likesCount() {
        return this.thread.likesCount || 0;
    }

    @computed get isNew() {
        return this.thread.isNew;
    }

    @computed get isLocked() {
        return this.thread.isLocked;
    }
}

interface Props {
    styles: typeof defaultStyles;
    threadViewModel: ThreadViewModel;
    borderTop?: boolean;
    key?: string;
}

@observer
class ThreadView extends React.Component<Props> {
    render() {
        const { threadViewModel, styles, borderTop } = this.props;

        const authorLink = threadViewModel.author
            ? new UserLink({
                  user: threadViewModel.author,
                  urlBuilder: URLBuilder.defaultURLBuilder,
              })
            : null;

        return (
            <tr className={classnames(styles.thread, { [styles.borderTop]: borderTop })} key={this.props.key}>
                <td className={styles.title}>
                    <LinkView link={threadViewModel.link} className={styles.link} />
                    {threadViewModel.isLocked ? <SVGIcon src={lock} className={styles.iconLock} /> : null}
                    {threadViewModel.isNew ? (
                        <span className={styles.highlight}>
                            <FormattedMessage id='NEW_THREAD' />
                        </span>
                    ) : null}

                    <div className={styles.iconWrapper}>
                        <SVGIcon style={{ image: styles.icon }} src={message} />
                        {threadViewModel.messagesCount || '0'}
                    </div>
                    <div className={styles.iconWrapper}>
                        <SVGIcon style={{ image: styles.icon }} src={users} />
                        {threadViewModel.contributorsCount || '0'}
                    </div>
                    <div className={styles.iconWrapper}>
                        <SVGIcon style={{ image: styles.icon }} src={like} />
                        {threadViewModel.likesCount || '0'}
                    </div>
                </td>
                <td className={styles.stats}>{threadViewModel.messagesCount || '0'}</td>
                <td className={styles.contributors}>{threadViewModel.contributorsCount || '0'}</td>
                <td className={styles.likes}>{threadViewModel.likesCount || '0'}</td>
                <td className={styles.linksWrapper}>
                    <LinkView link={threadViewModel.lastMessageLink} className={styles.date}>
                        <FormattedDateAgoView value={threadViewModel.date} />
                        <SVGIcon style={{ image: styles.icon }} src={arrowEnter} />
                    </LinkView>
                    <br />
                    <SVGPureIcon src={user} className={styles.userIcon} />
                    <LinkView link={authorLink} className={styles.user} />
                </td>
            </tr>
        );
    }
}

export default Styled(defaultStyles)(ThreadView);
